import { useMemo } from 'react';
import { getIsDataSourceAnEntityType } from '../../utils/entityDataSource';
import { useDataSourceRecordStore } from './DataSourceRecordStoreProvider';
import type { UnsavedDataSource } from './types';

export const useDataSourceRecords = () => {
  return useDataSourceRecordStore().use.dataSources();
};

export const useUnsavedDataSourceRecords = () => {
  return useDataSourceRecordStore().use.unsavedDataSources();
};

export const useDataSourceRecordsSchemas = () => {
  return useDataSourceRecordStore().use.dataSourceSchemas();
};

export const useDataSourceExists = () => {
  const getState = useDataSourceRecordStore().getState;
  const dataSourceExists = (dataSourceId: string) => {
    const dataSources = getState().dataSources;
    return Boolean(dataSources[dataSourceId]) || getIsDataSourceAnEntityType(dataSourceId);
  };

  return { dataSourceExists };
};

export const useGetDataSourceRecordStoreState = () => {
  const getDataSourceRecordStoreState = useDataSourceRecordStore().getState;
  return { getDataSourceRecordStoreState };
};

/**
 * Merges the data sources from the store with the unsaved data sources.
 * This is done because in the data source listing panel, we show the saved and unsaved data sources
 * and we want to override the saved data sources with the unsaved data sources when they exist.
 */
export function useMergedDataSources() {
  const storeDataSources = useDataSourceRecords();
  const unsavedDataSources = useUnsavedDataSourceRecords();

  const adaptedDataSources = useMemo(() => {
    const allDataSourcesKeys = Object.keys(storeDataSources);
    return allDataSourcesKeys.reduce<Record<string, UnsavedDataSource | undefined>>((acc, id) => {
      acc[id] = unsavedDataSources[id] || storeDataSources[id];
      return acc;
    }, {});
  }, [storeDataSources, unsavedDataSources]);

  return { dataSources: adaptedDataSources };
}
